import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { darkTheme } from './theme';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Register from './pages/Register';
import Clients from './pages/Clients';
import ClientOnboarding from './pages/ClientOnboarding';
import Gallery from './pages/Gallery';
import Campaigns from './pages/Campaigns';
import InstagramAutomation from './pages/InstagramAutomation'; // Added import
import Posts from './pages/Posts';
import Analytics from './pages/Analytics';
import Reputation from './pages/Reputation';
import AIContent from './pages/AIContent';
import Competitors from './pages/Competitors';
import MLInsights from './pages/MLInsights';
import Reports from './pages/Reports';
import ModelReport from './pages/ModelReport';
import Settings from './pages/Settings';
import TaskMonitoring from './pages/TaskMonitoring';
import GroupSharing from './pages/GroupSharing';
import ImageDirectories from './pages/ImageDirectories';
import Docs from './pages/Docs';
import Scheduling from './pages/Scheduling';
import ScheduledPosts from './pages/ScheduledPosts';
import FacebookOAuthCallback from './pages/FacebookOAuthCallback';
import XOAuthCallback from './pages/XOAuthCallback';
import YouTubeOAuthCallback from './pages/YouTubeOAuthCallback';
import TumblrOAuthCallback from './pages/TumblrOAuthCallback';
import InstagramOAuthCallback from './pages/InstagramOAuthCallback';
import LinkedInOAuthCallback from './pages/LinkedInOAuthCallback';
import UserDataDeletion from './pages/UserDataDeletion';
import DataDeletionStatus from './pages/DataDeletionStatus';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ClientProvider } from './contexts/ClientContext';
import Layout from './components/Layout';
import {
  SHOW_COMPETITORS_UI,
  SHOW_GROUP_SHARING,
  SHOW_INSTAGRAM_UI,
} from './config/featureFlags';

function RedirectClientFilesToGallery() {
  const { clientId } = useParams();
  return (
    <Navigate
      to={`/gallery?client_id=${encodeURIComponent(clientId)}`}
      replace
    />
  );
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#0d1117',
          color: '#f0f6fc',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '3px solid #30363d',
              borderTop: '3px solid #58a6ff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          ></div>
          <span style={{ color: '#8b949e' }}>Loading...</span>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

function App() {
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <AuthProvider>
        <ToastProvider>
          <ClientProvider>
            <NotificationProvider>
              <Router>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Dashboard />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/clients/:clientId/files"
                  element={
                    <ProtectedRoute>
                      <RedirectClientFilesToGallery />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/gallery"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Gallery />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/clients"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Clients />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/clients/:clientId/onboarding"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ClientOnboarding />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/campaigns"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Campaigns />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/instagram-automation"
                  element={
                    <ProtectedRoute>
                      {SHOW_INSTAGRAM_UI ? (
                        <Layout>
                          <InstagramAutomation />
                        </Layout>
                      ) : (
                        <Navigate to="/" replace />
                      )}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/posts"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Posts />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/scheduling"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Scheduling />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/scheduled-posts"
                  element={
                    <ProtectedRoute>
                      <ScheduledPosts />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/schedule"
                  element={<Navigate to="/scheduling" replace />}
                />
                <Route
                  path="/group-sharing"
                  element={
                    <ProtectedRoute>
                      {SHOW_GROUP_SHARING ? (
                        <Layout>
                          <GroupSharing />
                        </Layout>
                      ) : (
                        <Navigate to="/" replace />
                      )}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/image-directories"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ImageDirectories />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ai-content"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <AIContent />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reputation"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Reputation />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Analytics />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/competitors"
                  element={
                    <ProtectedRoute>
                      {SHOW_COMPETITORS_UI ? (
                        <Layout>
                          <Competitors />
                        </Layout>
                      ) : (
                        <Navigate to="/" replace />
                      )}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ml-insights"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <MLInsights />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Reports />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/model"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ModelReport />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/docs"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Docs />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/docs/:topic"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Docs />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Settings />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/monitoring"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <TaskMonitoring />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/facebook-oauth-callback"
                  element={<FacebookOAuthCallback />}
                />
                <Route
                  path="/linkedin-oauth-callback"
                  element={<LinkedInOAuthCallback />}
                />
                <Route path="/x-oauth-callback" element={<XOAuthCallback />} />
                <Route
                  path="/youtube-oauth-callback"
                  element={<YouTubeOAuthCallback />}
                />
                <Route
                  path="/tumblr-oauth-callback"
                  element={<TumblrOAuthCallback />}
                />
                <Route
                  path="/instagram-oauth-callback"
                  element={
                    SHOW_INSTAGRAM_UI ? (
                      <InstagramOAuthCallback />
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />
                {/* Public legal / Meta URLs — must match paths so SPA fallback does not show a blank screen */}
                <Route path="/data-deletion.html" element={<UserDataDeletion />} />
                <Route path="/data-deletion" element={<UserDataDeletion />} />
                <Route path="/data-deletion-status.html" element={<DataDeletionStatus />} />
                <Route path="/data-deletion-status" element={<DataDeletionStatus />} />
                <Route path="/privacy-policy.html" element={<PrivacyPolicy />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service.html" element={<TermsOfService />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/terms" element={<TermsOfService />} />
              </Routes>
              </Router>
            </NotificationProvider>
          </ClientProvider>
        </ToastProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
